import Link from 'next/link'

import { cn } from '@/lib/utils'

import { Badge } from '@/components/ui/badge'

import { FooterConfig } from '@/types/header-footer-config'
import { FooterItem } from '@/types/nav.type'

interface FooterLinkGroupsProps {
  groups: FooterItem[]
  containerWidth?: FooterConfig['containerWidth']
  spacing?: FooterConfig['spacing']
}

const containerClassNames: Record<NonNullable<FooterConfig['containerWidth']>, string> = {
  content: 'container',
  wide: 'mx-auto w-full max-w-6xl px-4 md:px-6',
  full: 'w-full px-4 md:px-6',
}

const spacingClassNames: Record<NonNullable<FooterConfig['spacing']>, string> = {
  compact: 'gap-4 py-4 sm:py-6',
  normal: 'gap-6 py-6 sm:py-8 md:py-10',
  relaxed: 'gap-8 py-8 sm:py-10 md:py-12',
}

export default function FooterLinkGroups({
  groups,
  containerWidth = 'content',
  spacing = 'normal',
}: FooterLinkGroupsProps) {
  if (!groups.length) return null

  return (
    <div
      className={cn(
        containerClassNames[containerWidth],
        'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
        spacingClassNames[spacing]
      )}
    >
      {groups.map(section => (
        <div key={section.title}>
          <span className="text-sm font-medium text-foreground">{section.title}</span>
          <ul className="mt-4 list-inside space-y-3">
            {section.items?.map(link => (
              <li key={`${section.title}-${link.title}`}>
                <Link
                  href={link.href}
                  className="text-sm text-muted-foreground hover:text-primary"
                  target={link.external ? '_blank' : undefined}
                  rel={link.external ? 'noopener noreferrer' : undefined}
                >
                  {link.title}
                </Link>
                {link.badge && (
                  <Badge variant="outline" className="ml-2 border-primary text-xs text-primary">
                    {link.badge}
                  </Badge>
                )}
              </li>
            ))}
          </ul>
        </div>
      ))}
    </div>
  )
}
